import React from 'react';
import LegalPageLayout, { Section, Paragraph, List, Highlight } from '../../components/LegalPageLayout';

const PrivacyPolicy = () => {
    return (
        <LegalPageLayout title="Privacy Policy" lastUpdated="March 2026">
            <Section title="Introduction">
                <Paragraph>
                    ClientCheck is committed to protecting your privacy and handling your data responsibly. 
                    This Privacy Policy explains how we collect, use, and safeguard information when you 
                    use our platform.
                </Paragraph>
            </Section>

            <Section title="Data We Collect">
                <Paragraph>
                    ClientCheck collects the following types of information:
                </Paragraph>
                
                <h3 className="font-medium text-foreground mt-4 mb-2">Account Information</h3>
                <List items={[
                    "Email address",
                    "Password (stored in encrypted form)",
                    "Company name",
                    "Trade or profession type"
                ]} />
                
                <h3 className="font-medium text-foreground mt-4 mb-2">Contractor Profile Information</h3>
                <List items={[
                    "Business details you choose to provide",
                    "Account creation date",
                    "Trust level and review history"
                ]} />
                
                <h3 className="font-medium text-foreground mt-4 mb-2">Job Address Data</h3>
                <List items={[
                    "Street addresses associated with reviews",
                    "City, state, and zip code information",
                    "Address experience summaries"
                ]} />
                
                <h3 className="font-medium text-foreground mt-4 mb-2">Review Submissions</h3>
                <List items={[
                    "Structured ratings across multiple categories",
                    "Job type and project size information",
                    "Job dates and outcome indicators",
                    "Optional notes (limited to 250 characters)"
                ]} />
                
                <h3 className="font-medium text-foreground mt-4 mb-2">Moderation and Audit Records</h3>
                <List items={[
                    "Content moderation decisions",
                    "Account activity logs",
                    "System access records for security purposes"
                ]} />
            </Section>

            <Section title="How We Use Your Data">
                <Paragraph>
                    ClientCheck uses collected data for the following purposes:
                </Paragraph>
                <List items={[
                    "Operating and maintaining the platform",
                    "Calculating contractor experience summaries and contractor trust levels",
                    "Enforcing community standards and content policies",
                    "Improving platform functionality and user experience",
                    "Communicating important platform updates",
                    "Ensuring platform security and preventing abuse",
                    "Complying with legal obligations"
                ]} />
            </Section>

            <Section title="Data Sharing">
                <Paragraph>
                    ClientCheck does not sell your personal information to third parties. We may share 
                    data in the following limited circumstances:
                </Paragraph>
                <List items={[
                    "With service providers who assist in platform operations (under strict confidentiality)",
                    "When required by law or valid legal process",
                    "To protect the rights, safety, or property of ClientCheck or our users",
                    "In connection with a business transfer such as a merger or acquisition"
                ]} />
                <Paragraph>
                    Review content you submit may be visible to other contractors using the platform. 
                    We do not publicly display your personal contact information.
                </Paragraph>
            </Section>

            <Section title="Data Security">
                <Paragraph>
                    ClientCheck implements reasonable security measures to protect your data, including:
                </Paragraph>
                <List items={[
                    "Encryption of sensitive data in transit and at rest",
                    "Secure password hashing",
                    "Access controls limiting data access to authorized personnel",
                    "Regular security assessments and updates"
                ]} />
                <Highlight>
                    While we take security seriously, no system is completely secure. You are 
                    responsible for maintaining the confidentiality of your account credentials.
                </Highlight>
            </Section>

            <Section title="Data Retention">
                <Paragraph>
                    ClientCheck retains data according to the following guidelines:
                </Paragraph>
                <List items={[
                    "Active account data is retained while your account remains active",
                    "Review data may be retained for the operational life of the platform",
                    "Archived reviews (from ownership resets) may be retained for moderation purposes",
                    "Audit logs may be retained for compliance and security purposes",
                    "Deleted account data may be retained in backups for a limited period"
                ]} />
                <Paragraph>
                    You may request deletion of your account by contacting our support team. 
                    Certain data may be retained as required by law or for legitimate business purposes.
                </Paragraph>
            </Section>

            <Section title="Your Rights">
                <Paragraph>
                    Depending on your location, you may have certain rights regarding your data:
                </Paragraph>
                <List items={[
                    "Access your personal data we hold",
                    "Request correction of inaccurate data",
                    "Request deletion of your data (subject to retention requirements)",
                    "Object to certain processing activities",
                    "Export your data in a portable format"
                ]} />
                <Paragraph>
                    To exercise these rights, please contact our support team.
                </Paragraph>
            </Section>

            <Section title="Cookies and Tracking">
                <Paragraph>
                    ClientCheck uses essential cookies and local storage for:
                </Paragraph>
                <List items={[
                    "Maintaining your login session",
                    "Storing your theme preference (light/dark mode)",
                    "Ensuring platform functionality"
                ]} />
                <Paragraph>
                    We do not use tracking cookies for advertising purposes.
                </Paragraph>
            </Section>

            <Section title="Changes to This Policy">
                <Paragraph>
                    ClientCheck may update this Privacy Policy as our practices evolve. We will notify 
                    you of significant changes by updating the 'Last Updated' date and, for material 
                    changes, through platform notifications.
                </Paragraph>
            </Section>

            <Section title="Contact Us">
                <Paragraph>
                    For privacy-related inquiries or to exercise your data rights, please contact 
                    our support team through the Contact page on our platform.
                </Paragraph>
            </Section>
        </LegalPageLayout>
    );
};

export default PrivacyPolicy;
